import React, { useState, useEffect } from "react";
import RiskAssessmentAPI from "../services/RiskAssessmentAPI";
import UserAPI from "../services/UserAPI";
import { getCookie } from "../utils/CookieUtil";
import { formatDateTime } from "../utils/DateTimeUtil";
import "./css/RiskScoresGenerator.css";
import LoadingSpinner from "../common/LoadingSpinner";

function RiskScoresGenerator() {
  const [selectedUser, setSelectedUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [individualLoading, setIndividualLoading] = useState(false);
  const [groupLoading, setGroupLoading] = useState(false);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [individualResult, setIndividualResult] = useState(null);
  const [groupResult, setGroupResult] = useState(null);
  const [allUsersResult, setAllUsersResult] = useState(null);
  const [errors, setErrors] = useState({});

  // Load user from cookie on component mount
  useEffect(() => {
    const loadUserFromCookie = async () => {
      try {
        const userUuid = getCookie("user");

        if (!userUuid) {
          setErrors((prev) => ({
            ...prev,
            general: "No user selected. Please select a user first.",
          }));
          setUserLoading(false);
          return;
        }

        console.log(
          "[RiskScoresGenerator] Loading user from cookie:",
          userUuid,
        );

        // Fetch user details using the UUID
        const response = await UserAPI.getUserByUuid(userUuid);
        const userData = response.data || response;

        if (userData) {
          setSelectedUser({
            userUuid: userUuid,
            userId: userData.userId,
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
          });
          console.log(
            "[RiskScoresGenerator] Loaded user:",
            userData.firstName,
            userData.lastName,
          );
        } else {
          throw new Error("User data not found");
        }
      } catch (error) {
        console.error(
          "[RiskScoresGenerator] Error loading user from cookie:",
          error,
        );
        setErrors((prev) => ({
          ...prev,
          general: "Failed to load user information.",
        }));
      } finally {
        setUserLoading(false);
      }
    };

    loadUserFromCookie();
  }, []);

  // Helper function to clear previous results and errors
  const clearResults = (type) => {
    if (type === "individual") {
      setIndividualResult(null);
      setErrors((prev) => ({ ...prev, individual: null }));
    } else if (type === "group") {
      setGroupResult(null);
      setErrors((prev) => ({ ...prev, group: null }));
    } else if (type === "allUsers") {
      setAllUsersResult(null);
      setErrors((prev) => ({ ...prev, allUsers: null }));
    }
  };
  // Calculate individual user risk score
  const handleCalculateIndividual = async () => {
    clearResults("individual");
    setIndividualLoading(true);

    try {
      if (!selectedUser || !selectedUser.userUuid) {
        throw new Error("No user selected for risk calculation");
      }

      const targetUserUuid = selectedUser.userUuid;

      console.log(
        "[GenerateRiskScores] Calculating individual risk for user:",
        targetUserUuid,
      );
      const response =
        await RiskAssessmentAPI.calculateUserRisk(targetUserUuid);

      if (response && response.success) {
        setIndividualResult({
          success: true,
          message: response.message || "Risk score calculated successfully",
          data: response.data,
          userUuid: response.userUuid || targetUserUuid,
        });
      } else {
        throw new Error(response?.message || "Failed to calculate risk score");
      }
    } catch (error) {
      console.error(
        "[GenerateRiskScores] Error calculating individual risk:",
        error,
      );
      setErrors((prev) => ({ ...prev, individual: error.message }));
    } finally {
      setIndividualLoading(false);
    }
  }; // Calculate group risk score based on selected user's group
  const handleCalculateGroup = async () => {
    clearResults("group");
    setGroupLoading(true);

    try {
      if (!selectedUser || !selectedUser.userUuid) {
        throw new Error("No user selected for group risk calculation");
      }

      const targetUserUuid = selectedUser.userUuid;

      console.log(
        "[GenerateRiskScores] Calculating group risk for user UUID:",
        targetUserUuid,
      );
      const response =
        await RiskAssessmentAPI.calculateUserRiskWithGroup(targetUserUuid);
      if (response && response.success) {
        setGroupResult({
          success: true,
          message:
            response.message || "Group risk score calculated successfully",
          data: response.data,
          userUuid: response.userUuid || targetUserUuid,
        });
      } else {
        throw new Error(
          response?.message || "Failed to calculate group risk score",
        );
      }
    } catch (error) {
      console.error(
        "[GenerateRiskScores] Error calculating group risk:",
        error,
      );
      setErrors((prev) => ({ ...prev, group: error.message }));
    } finally {
      setGroupLoading(false);
    }
  };

  // Calculate risk scores for all users (admin function)
  const handleCalculateAllUsers = async () => {
    clearResults("allUsers");
    setAllUsersLoading(true);

    try {
      // This would require getting all users first, then calculating each
      // For now, we'll use a placeholder approach or call a batch endpoint if available
      console.log("[GenerateRiskScores] Calculating risk scores for all users");

      // TODO: This might need a specific batch endpoint or iteration through all users
      // For now, we'll show a placeholder response
      setAllUsersResult({
        success: true,
        message: "Batch risk calculation initiated for all users",
        data: {
          status: "In Progress",
          note: "This feature requires a batch processing endpoint",
        },
      });
    } catch (error) {
      console.error(
        "[GenerateRiskScores] Error calculating all users risk:",
        error,
      );
      setErrors((prev) => ({ ...prev, allUsers: error.message }));
    } finally {
      setAllUsersLoading(false);
    }
  };

  const formatResultData = (data) => {
    if (!data) return null;

    return (
      <div className="result-data">
        {data.riskScore && (
          <p>
            <strong>Risk Score:</strong> {Math.round(data.riskScore)}%
          </p>
        )}
        {data.riskLevel && (
          <p>
            <strong>Risk Level:</strong> {data.riskLevel}
          </p>
        )}
        {data.calculatedAt && (
          <p>
            <strong>Calculated At:</strong> {formatDateTime(data.calculatedAt)}
          </p>
        )}
        {data.factors && (
          <div>
            <strong>Risk Factors:</strong>
            <ul>
              {Object.entries(data.factors).map(([key, value]) => (
                <li key={key}>
                  {key}:{" "}
                  {typeof value === "number"
                    ? Math.round(value * 100) / 100
                    : value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="generate-risk-scores">
      <h2>Generate Risk Scores</h2>
      <p className="description">
        Calculate risk assessment scores for individual users, groups, or all
        users in the system.
      </p>

      {/* Loading state */}
      {userLoading && (
        <div className="loading-container">
          <LoadingSpinner
            size="sm"
            message="Loading user information..."
            className="compact"
          />
        </div>
      )}

      {/* General error */}
      {errors.general && (
        <div className="result-container error">
          <h4>Error</h4>
          <p className="error-message">{errors.general}</p>
        </div>
      )}

      {/* Show content only when user is loaded */}
      {!userLoading && selectedUser && (
        <>
          {/* Selected User Info */}
          <div className="selected-user-info">
            <h3>Selected User</h3>
            <p>
              <strong>
                {selectedUser.firstName} {selectedUser.lastName}
              </strong>
              {selectedUser.email && <span> ({selectedUser.email})</span>}
            </p>
          </div>

          {/* Individual User Risk Calculation */}
          <div className="risk-section">
            <h3>Individual User Risk</h3>
            <p className="section-description">
              Calculate risk score for {selectedUser.firstName}{" "}
              {selectedUser.lastName}.
            </p>

            <button
              className="calculate-btn individual-btn"
              onClick={handleCalculateIndividual}
              disabled={individualLoading}
            >
              {individualLoading
                ? "Calculating..."
                : "Calculate Individual Risk"}
            </button>

            {individualResult && (
              <div className="result-container success">
                <h4>Individual Risk Calculation Result</h4>
                <p className="success-message">{individualResult.message}</p>
                {formatResultData(individualResult.data)}
              </div>
            )}

            {errors.individual && (
              <div className="result-container error">
                <h4>Error</h4>
                <p className="error-message">{errors.individual}</p>
              </div>
            )}
          </div>

          {/* Group Risk Calculation */}
          <div className="risk-section">
            <h3>Group Risk (Based on Selected User)</h3>
            <p className="section-description">
              Calculate risk score for the group that {selectedUser.firstName}{" "}
              {selectedUser.lastName} belongs to.
            </p>

            <button
              className="calculate-btn group-btn"
              onClick={handleCalculateGroup}
              disabled={groupLoading}
            >
              {groupLoading ? "Calculating..." : "Calculate Group Risk"}
            </button>

            {groupResult && (
              <div className="result-container success">
                <h4>Group Risk Calculation Result</h4>
                <p className="success-message">{groupResult.message}</p>
                {formatResultData(groupResult.data)}
              </div>
            )}

            {errors.group && (
              <div className="result-container error">
                <h4>Error</h4>
                <p className="error-message">{errors.group}</p>
              </div>
            )}
          </div>

          {/* All Users Risk Calculation */}
          <div className="risk-section">
            <h3>All Users Risk</h3>
            <p className="section-description">
              Calculate risk scores for all users in the system (Admin
              function).
            </p>

            <button
              className="calculate-btn all-users-btn"
              onClick={handleCalculateAllUsers}
              disabled={allUsersLoading}
            >
              {allUsersLoading ? "Calculating..." : "Calculate All Users Risk"}
            </button>

            {allUsersResult && (
              <div className="result-container success">
                <h4>All Users Risk Calculation Result</h4>
                <p className="success-message">{allUsersResult.message}</p>
                {formatResultData(allUsersResult.data)}
              </div>
            )}

            {errors.allUsers && (
              <div className="result-container error">
                <h4>Error</h4>
                <p className="error-message">{errors.allUsers}</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default RiskScoresGenerator;
